import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DraftCostBreakdown from './DraftCostBreakdown';
import {
  searchEmailActivity,
  getEmailActivityDetail,
  EmailActivityItem,
  EmailActivitySearchParams,
  EmailTemplateInfo,
} from '../api/emailActivity';

const PAGE_LIMIT = 50;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'sent', label: 'Sent' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'soft_bounced', label: 'Soft bounced' },
  { value: 'hard_bounced', label: 'Hard bounced' },
];

const ENGAGEMENT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'opened', label: 'Opened' },
  { value: 'clicked', label: 'Clicked' },
  { value: 'none', label: 'No engagement' },
];

const EMPTY_FILTERS = {
  recipient: '',
  subject: '',
  claim: '',
  status: '',
  engagement: '',
  date_from: '',
  date_to: '',
};

type Filters = typeof EMPTY_FILTERS;

const statusColor = (status: string | null): string => {
  if (status === 'delivered') return '#0f9d58';
  if (status === 'hard_bounced' || status === 'soft_bounced') return '#d93025';
  return '#5f6368';
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB');
  } catch {
    return iso;
  }
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #ccc',
  borderRadius: 6,
  fontSize: 14,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#5f6368',
  marginBottom: 4,
};

const EmailActivityPage: React.FC = () => {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  // The filters that produced the current result set. Pagination must use these,
  // NOT the live `filters` bound to the inputs (which may be unconfirmed edits).
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<EmailActivityItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<EmailActivityItem | null>(null);
  const [template, setTemplate] = useState<EmailTemplateInfo | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  const runSearch = useCallback(async (searchFilters: Filters, targetPage: number) => {
    setLoading(true);
    setError(null);
    const params: EmailActivitySearchParams = {
      page: targetPage,
      limit: PAGE_LIMIT,
    };
    if (searchFilters.recipient.trim()) params.recipient = searchFilters.recipient.trim();
    if (searchFilters.subject.trim()) params.subject = searchFilters.subject.trim();
    if (searchFilters.claim.trim()) params.claim = searchFilters.claim.trim();
    if (searchFilters.status) params.status = searchFilters.status;
    if (searchFilters.engagement) params.engagement = searchFilters.engagement;
    if (searchFilters.date_from) params.date_from = searchFilters.date_from;
    if (searchFilters.date_to) params.date_to = searchFilters.date_to;

    try {
      const response = await searchEmailActivity(params);
      setItems(response.items);
      setTotal(response.total);
      setPage(response.page);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to search email activity');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount with empty filters, page 1
  useEffect(() => {
    runSearch(EMPTY_FILTERS, 1);
  }, [runSearch]);

  // Whenever a row is selected, fetch the full detail (richer item + template
  // preview). The row-derived `item` is shown immediately; this call fills in
  // anything the search response doesn't carry.
  useEffect(() => {
    if (!selected) {
      setTemplate(null);
      return;
    }
    let cancelled = false;
    setTemplateLoading(true);
    getEmailActivityDetail(selected.id)
      .then(detail => {
        if (cancelled) return;
        setSelected(detail.item);
        setTemplate(detail.template);
      })
      .catch(() => {
        if (!cancelled) setTemplate(null);
      })
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const handleField = (field: keyof Filters) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFilters(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    runSearch(filters, 1);
  };

  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    runSearch(EMPTY_FILTERS, 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const goToPage = (targetPage: number) => {
    if (targetPage < 1 || targetPage > totalPages) return;
    runSearch(appliedFilters, targetPage);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#202124' }}>Email Activity</h1>
        <p style={{ margin: '4px 0 0', color: '#5f6368', fontSize: 14 }}>
          Search MailerSend delivery and engagement history.
        </p>
      </div>

      <DraftCostBreakdown />
      {/* Filters */}
      <div
        style={{
          background: 'white',
          padding: 20,
          borderRadius: 8,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
          }}
        >
          <div>
            <label style={labelStyle}>Recipient</label>
            <input
              type="text"
              value={filters.recipient}
              onChange={handleField('recipient')}
              onKeyDown={handleKeyDown}
              placeholder="email@example.com"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Subject</label>
            <input
              type="text"
              value={filters.subject}
              onChange={handleField('subject')}
              onKeyDown={handleKeyDown}
              placeholder="Subject contains…"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Claim ref</label>
            <input
              type="text"
              value={filters.claim}
              onChange={handleField('claim')}
              onKeyDown={handleKeyDown}
              placeholder="Claim ref (e.g. CL103828.001)"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select
              value={filters.status}
              onChange={handleField('status')}
              style={{ ...inputStyle, width: '100%' }}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Engagement</label>
            <select
              value={filters.engagement}
              onChange={handleField('engagement')}
              style={{ ...inputStyle, width: '100%' }}
            >
              {ENGAGEMENT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Date from</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={handleField('date_from')}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Date to</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={handleField('date_to')}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button
            onClick={handleSearch}
            disabled={loading}
            style={{
              padding: '8px 20px',
              background: '#1a73e8',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            Search
          </button>
          <button
            onClick={handleClear}
            disabled={loading}
            style={{
              padding: '8px 20px',
              background: 'white',
              color: '#5f6368',
              border: '1px solid #ccc',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Results count */}
      <div style={{ marginBottom: 12, color: '#5f6368', fontSize: 14 }}>
        {loading ? 'Loading…' : `${total} emails`}
      </div>

      {error && (
        <div
          style={{
            background: '#fce8e6',
            color: '#d93025',
            padding: '12px 16px',
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Results table */}
      <div
        style={{
          background: 'white',
          borderRadius: 8,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Recipient</th>
                <th style={thStyle}>Subject</th>
                <th style={thStyle}>Engagement</th>
                <th style={thStyle}>Claim</th>
                <th style={thStyle}>Template</th>
                <th style={thStyle}>Date</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#5f6368' }}>
                    No emails found
                  </td>
                </tr>
              )}
              {items.map(item => (
                <tr
                  key={item.id}
                  onClick={() => setSelected(item)}
                  style={{
                    borderTop: '1px solid #eee',
                    cursor: 'pointer',
                    background: selected?.id === item.id ? '#e8f0fe' : 'white',
                  }}
                >
                  <td style={tdStyle}>
                    <span style={{ color: statusColor(item.status), fontWeight: 600 }}>
                      {item.status || '—'}
                    </span>
                  </td>
                  <td style={tdStyle}>{item.recipient || '—'}</td>
                  <td style={tdStyle}>{item.subject || '—'}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {item.opened && <span style={badgeStyle('#0f9d58')}>Opened</span>}
                      {item.clicked && <span style={badgeStyle('#1a73e8')}>Clicked</span>}
                      {!item.opened && !item.clicked && <span style={{ color: '#9aa0a6' }}>—</span>}
                    </div>
                  </td>
                  <td style={tdStyle}>{item.claimId || '—'}</td>
                  <td style={tdStyle}>{item.templateName || ''}</td>
                  <td style={tdStyle}>{formatDateTime(item.sentAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          marginTop: 16,
        }}
      >
        <button
          onClick={() => goToPage(page - 1)}
          disabled={loading || page <= 1}
          style={pagerStyle(loading || page <= 1)}
        >
          Prev
        </button>
        <span style={{ color: '#5f6368', fontSize: 14 }}>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => goToPage(page + 1)}
          disabled={loading || page >= totalPages}
          style={pagerStyle(loading || page >= totalPages)}
        >
          Next
        </button>
      </div>

      {/* Detail slide-over */}
      {selected && (
        <>
          <div
            onClick={() => setSelected(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 1000,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 'min(480px, 90vw)',
              background: 'white',
              boxShadow: '-2px 0 12px rgba(0,0,0,0.15)',
              zIndex: 1001,
              overflowY: 'auto',
              padding: 24,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={{ margin: 0, fontSize: 18, color: '#202124', paddingRight: 12 }}>
                {selected.subject || '(no subject)'}
              </h2>
              <button
                onClick={() => setSelected(null)}
                aria-label="Close detail"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  lineHeight: 1,
                  cursor: 'pointer',
                  color: '#5f6368',
                }}
              >
                ×
              </button>
            </div>

            <dl style={{ margin: '20px 0', fontSize: 14 }}>
              <DetailRow label="To" value={selected.recipient} />
              <DetailRow label="From" value={selected.from} />
              <DetailRow
                label="Status"
                value={
                  <span style={{ color: statusColor(selected.status), fontWeight: 600 }}>
                    {selected.status || '—'}
                  </span>
                }
              />
              <DetailRow label="Sent" value={formatDateTime(selected.sentAt)} />
              {selected.claimId && (
                <DetailRow
                  label="Claim"
                  value={
                    <Link
                      to={`/?search=${encodeURIComponent(selected.claimId)}`}
                      style={{ color: '#1a73e8', textDecoration: 'none' }}
                    >
                      {selected.claimId}
                    </Link>
                  }
                />
              )}
              {selected.tags.length > 0 && (
                <DetailRow label="Tags" value={selected.tags.join(', ')} />
              )}
              <DetailRow label="Email ID" value={selected.id ? <Mono>{selected.id}</Mono> : null} />
              <DetailRow
                label="Message ID"
                value={selected.messageId ? <Mono>{selected.messageId}</Mono> : null}
              />
              <DetailRow
                label="Template"
                value={selected.templateName || template?.name || null}
              />
              <DetailRow
                label="Template ID"
                value={selected.templateId ? <Mono>{selected.templateId}</Mono> : null}
              />
            </dl>

            <h3 style={{ fontSize: 14, color: '#202124', margin: '0 0 8px' }}>Template preview</h3>
            {templateLoading ? (
              <p style={{ color: '#9aa0a6', fontSize: 14 }}>Loading template…</p>
            ) : template ? (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 14, color: '#202124', margin: '0 0 8px', fontWeight: 600 }}>
                  {template.name || '(unnamed template)'}
                </p>
                {template.imagePath && (
                  <img
                    src={template.imagePath}
                    alt={`Preview of template ${template.name || template.templateId}`}
                    style={{ maxWidth: '100%', border: '1px solid #eee', borderRadius: 4, marginBottom: 8 }}
                  />
                )}
                {template.html && (
                  <iframe
                    sandbox=""
                    srcDoc={template.html}
                    style={{ width: '100%', height: 400, border: '1px solid #eee' }}
                    title="Email template preview"
                  />
                )}
                {!template.imagePath && !template.html && (
                  <p style={{ color: '#9aa0a6', fontSize: 14 }}>No preview available for this template.</p>
                )}
              </div>
            ) : selected.templateId ? (
              <p style={{ color: '#9aa0a6', fontSize: 14, marginBottom: 20 }}>
                Template content unavailable.
              </p>
            ) : (
              <p style={{ color: '#9aa0a6', fontSize: 14, marginBottom: 20 }}>
                No template recorded for this email.
              </p>
            )}

            <h3 style={{ fontSize: 14, color: '#202124', margin: '0 0 8px' }}>Event timeline</h3>
            {selected.events.length === 0 ? (
              <p style={{ color: '#9aa0a6', fontSize: 14 }}>No events recorded.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {selected.events.map((event, idx) => (
                  <li
                    key={`${event.type}-${event.at}-${idx}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 0',
                      borderTop: idx === 0 ? 'none' : '1px solid #eee',
                      fontSize: 14,
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#202124' }}>{event.type}</span>
                    <span style={{ color: '#5f6368' }}>{formatDateTime(event.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontWeight: 600,
  color: '#5f6368',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  color: '#202124',
  verticalAlign: 'top',
};

const badgeStyle = (color: string): React.CSSProperties => ({
  background: color,
  color: 'white',
  borderRadius: 12,
  padding: '2px 10px',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

const pagerStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 20px',
  background: 'white',
  color: disabled ? '#bdc1c6' : '#1a73e8',
  border: '1px solid #ccc',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: disabled ? 'default' : 'pointer',
});

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 12, padding: '6px 0' }}>
    <dt style={{ width: 70, flexShrink: 0, color: '#5f6368', fontWeight: 600 }}>{label}</dt>
    <dd style={{ margin: 0, color: '#202124', wordBreak: 'break-word' }}>{value || '—'}</dd>
  </div>
);

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{children}</span>
);

export default EmailActivityPage;
